import { useTracking } from 'react-tracking';

import loadable from '@loadable/component';
import { useFlag } from '@/features/unleash';

import { Grid, Heading, Text } from '@simplywallst/ui-core';
import { Snowflake } from '@simplywallst/ui-charts/Snowflake';

import { formatterCurrencyTruncated } from '@/utilities/format';

import useCompanyFeed from '@/hooks/useCompanyFeed';
import { useNormalisedCompanySnowflakeScore } from '@/hooks/useNormalisedCompanySnowflakeScore';

import { Tile } from '../Tile';

import { ValuationMetrics } from './components/ValuationMetrics';
import { ImportantUpdates } from './components/ImportantUpdates';
import { OverviewSkeleton } from './components/OverviewSkeleton';
import { MoreInformationModal } from './components/MoreInformationModal';
import { RisksRewards } from './components/RisksRewards';

import { useCompanyRisksRewards } from './hooks/useCompanyRisksRewards';
import { useCompanyPriceTargetHistory } from './hooks/useCompanyPriceTargetHistory';
import { PriceTarget } from './components/PriceTarget';
import { Notes } from './components/Notes';
import { FullAnalysisLink } from '../FullAnalysisLink';
import { useShouldShowAddWatchlistButton } from './components/AddWatchlistButton';
import { useCompanyNotes } from '@/components/Note/hooks/useCompanyNotes';
import type { FragmentOf } from '@/utilities/gql';
import { gql } from '@/utilities/gql';

const AddWatchlistButton = loadable(
  () => import('./components/AddWatchlistButton')
);

export const MONITORING_PANEL_COMPANY_OVERVIEW_FRAGMENT = gql(`
  fragment MonitoringPanelCompanyOverviewFields on Company @_unmask {
    id
    tickerSymbol
    canonicalURL
    score {
      value
      future
      past
      health
      dividend
      sentence
    }
    analysisValue {
      marketCapUSD
      priceToSales
      pb
      pe
      preferredRelativeMultiple
      npvPerShare
      lastSharePrice
    }
    analysisDividend {
      dividendYield
    }
    analysisFuture {
      forwardPe1Y
      forwardPriceToSales1Y
      revenueGrowthAnnual
      netIncomeGrowthAnnual
    }
  }
`);

interface Props {
  company: FragmentOf<typeof MONITORING_PANEL_COMPANY_OVERVIEW_FRAGMENT>;
  selectTab: (tab: 'notes' | 'updates') => void;
}

export const Overview = ({ company, selectTab }: Props) => {
  const { Track } = useTracking({
    type: 'track',
    subject: 'Overview',
  });

  const monoNarrativeFeedFlag = useFlag({ name: 'mono-narrative-feed' });
  const isMonoNarrativeFeedFlagEnabled =
    monoNarrativeFeedFlag.flagsReady && monoNarrativeFeedFlag.enabled;
  const feedQuery = useCompanyFeed(company.canonicalURL, {
    types: isMonoNarrativeFeedFlagEnabled ? ['brief', 'narrative'] : ['brief'],
  });
  const companyNotes = useCompanyNotes(company.id);
  const statementsQuery = useCompanyRisksRewards(company.canonicalURL);
  const priceTargetHistoryQuery = useCompanyPriceTargetHistory({
    id: company.id,
    size: 2,
  });

  const shouldShowAddWatchlistButton = useShouldShowAddWatchlistButton();

  const points = useNormalisedCompanySnowflakeScore(company.score);

  if (
    feedQuery.isLoading ||
    companyNotes.isFetching ||
    statementsQuery.isLoading ||
    priceTargetHistoryQuery.isLoading
  ) {
    return <OverviewSkeleton />;
  }

  const feedItem = feedQuery.data?.feedItems[0];
  const [priceTarget, previousPriceTarget] =
    priceTargetHistoryQuery.data?.priceTargetHistory ?? [];
  const hasRisksRewards =
    statementsQuery.data.rewards.length > 0 ||
    statementsQuery.data.risks.length > 0;

  const {
    preferredRelativeMultiple = null,
    pe = null,
    pb = null,
    priceToSales = null,
    marketCapUSD = null,
  } = company.analysisValue || {};

  return (
    <Track>
      <Grid gap="8px">
        {feedQuery.data && feedItem ? (
          <Tile templateRows="repeat(2, max-content)" gap="16px">
            <ImportantUpdates
              selectTab={selectTab}
              company={feedQuery.data}
              feedItem={feedItem}
            />
          </Tile>
        ) : null}
        <Notes
          selectTab={selectTab}
          companyId={company.id}
          uniqueSymbol={company.tickerSymbol}
        />
        <Tile gap="4px">
          <Grid templateColumns="1fr max-content" gap="12px">
            <Grid templateRows="repeat(2, max-content)" gap="16px">
              <Grid templateRows="auto auto" gap="8px">
                <Heading
                  as="h3"
                  typography="tinyStrong"
                  color="whiteRGBA"
                  opacity={0.5}
                >
                  Snowflake Analysis
                </Heading>
                <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                  {company.score?.sentence ?? 'n/a'}
                </Text>
              </Grid>
              <Grid templateRows="auto auto" gap="2px">
                <Heading
                  as="h4"
                  typography="tiny"
                  color="whiteRGBA"
                  opacity={0.5}
                >
                  Market Cap
                </Heading>
                <Text typography="xSmall">
                  {formatterCurrencyTruncated(marketCapUSD, 'USD')}
                </Text>
              </Grid>
            </Grid>
            <Grid templateColumns="132px 16px">
              <Snowflake width={132} points={points}>
                <Snowflake.Labels />
              </Snowflake>
              <MoreInformationModal />
            </Grid>
          </Grid>
          <ValuationMetrics
            priceToSales={priceToSales}
            priceToBook={pb}
            priceToEarnings={pe}
            preferredRelativeMultiple={preferredRelativeMultiple}
            dividendYield={company.analysisDividend?.dividendYield ?? null}
            forwardPe1Y={company.analysisFuture?.forwardPe1Y ?? null}
            forwardPriceToSales1Y={
              company.analysisFuture?.forwardPriceToSales1Y ?? null
            }
            salesGrowth={company.analysisFuture?.revenueGrowthAnnual ?? null}
            earningsGrowth={
              company.analysisFuture?.netIncomeGrowthAnnual ?? null
            }
          />
          <PriceTarget
            priceTarget={priceTarget}
            previousPriceTarget={previousPriceTarget}
          />
          {shouldShowAddWatchlistButton ? (
            <AddWatchlistButton
              companyId={company.id}
              tickerSymbol={company.tickerSymbol}
            />
          ) : null}
        </Tile>
        {hasRisksRewards ? (
          <Tile gap="16px" data-cy-id="risks-rewards">
            <RisksRewards
              tickerSymbol={company.tickerSymbol}
              statements={statementsQuery.data}
            />
          </Tile>
        ) : null}

        <FullAnalysisLink canonicalURL={company.canonicalURL} />
      </Grid>
    </Track>
  );
};
